import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Building software is a distraction",
  "description": "",
  "published": "2019-09-20T00:00:00.000Z",
  "redirect_from": ["/blog/building-software-is-a-distraction/swizec/9200"],
  "categories": "Business, Side Projects, Startups",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`We often write code to distract ourselves from the fact we don't know what we're building.`}</p>
    <p>{`Yesterday I had a long conversation with a friend who's starting a company. Let's call him Matt because that's not his name.`}</p>
    <p>{`After months of thought, Matt quit his job and went looking for investors. He realized it was too early and he doesn't know shit. So he started really digging into his idea and his market.`}</p>
    <p>{`He uncovered a goldmine.`}</p>
    <p>{`A swathe of the economy that's growing, inefficient, and needs some help. His wonderful idea will grease the wheels, the economy will flourish, the consumers will love him, and he'll get filthy rich in the process 💪`}</p>
    <p>{`Matt wanted my help hiring an engineer. Or a designer. Someone, anyone, who could help him build the product.`}</p>
    <p>{`Matt is not technical, you see. His skill lies in business development, sales, and big picture thinking. The skills you `}<em parentName="p">{`really`}</em>{` need to succeed in the market he's targeting. The stuff us engineers run away from screaming.`}</p>
    <p><em parentName="p">{`"How do I find an engineer? Someone local, someone I can sit down and work with, who won't rip me off."`}</em></p>
    <p>{`🤔`}</p>
    <p>{`He's unfunded, bootstrapping, and on his own in San Francisco. Money is always tight when you have no income.`}</p>
    <p><em parentName="p">{`"Mate, you're looking for a cofounder?"`}</em></p>
    <p><em parentName="p">{`"Yeah but nobody I talked to wants to work for equity"`}</em></p>
    <p>{`Well yeah. You're a guy with an idea.`}</p>
    <p><em parentName="p">{`"Matt, what have you done to prove people will pay for this? Why do you think you're ready to build software?"`}</em></p>
    <p>{`Hint: he isn't.`}</p>
    <h2 {...{
      "id": "software-is-a-tool-not-a-goal"
    }}>{`Software is a tool, not a goal`}</h2>
    <p><img parentName="p" {...{
        "src": "https://buffer.com/resources/wp-content/uploads/2014/01/mario1.gif",
        "alt": null
      }}></img></p>
    <p>{`Through conversation we discovered that Matt wants to build software because his company doesn't feel real without it. You need `}<em parentName="p">{`something`}</em>{` that you're building. You can't just "be a company".`}</p>
    <p>{`But you can.`}</p>
    <p>{`A company isn't the software it sells. A company isn't the offices. It isn't the team. It isn't even anything in particular.`}</p>
    <p>{`A company is a set of processes. A machine.`}</p>
    <p>{`Some of those processes are encoded in software, some are done by people, some aren't even documented. `}<em parentName="p">{`That`}</em>{`'s a company. That set of processes and ideas.`}</p>
    <p>{`Software is just a tool.`}</p>
    <p>{`What Matt needs are customers. People whose problem his solving. People who are `}<em parentName="p">{`better people`}</em>{` because they use his service.`}</p>
    <p>{`That `}<strong parentName="p">{`service`}</strong>{` is what he needs to build. Not some nebulous piece software the definition of which he doesn't even know yet.`}</p>
    <p>{`I dug deeper.`}</p>
    <p><em parentName="p">{`"Matt, what do you `}<strong parentName="em">{`need`}</strong>{`? What's blocking you from making your first sale?"`}</em></p>
    <p>{`Well I need a website.`}</p>
    <p>{`Why?`}</p>
    <p>{`So I look real when I'm talking to people. So there's something to point them at.`}</p>
    <p>{`Why?`}</p>
    <p>{`Because it builds credibility. Without a website I'm just a random schmo.`}</p>
    <p>{`Okay that's fair. Credibility and authority. You don't need a designer or even an engineer for that. You definitely don't need to `}<em parentName="p">{`hire`}</em>{` anyone. Get an account on square space, carrd, or similar, pick the prettiest template you can find, and fill in your info.`}</p>
    <p>{`Voila, a website.`}</p>
    <p>{`You can even hire a freelancer to cobble it together for you. Don't waste money on a custom solution until you need one.`}</p>
    <p>{`Ok there's your website, you've got your credibility. What's next? Why do you need an engineer, Matt?`}</p>
    <p>{`Well I need to build the product.`}</p>
    <p>{`Why?`}</p>
    <p>{`... because I'm a product company? Because it isn't real without a product?`}</p>
    <p>{`Why?`}</p>
    <p>{`Err ...`}</p>
    <p>{`Matt, did anyone pay you yet? Do you know exactly what they need to make their jobs easier? Have you talked to them?`}</p>
    <p>{`Well I know they have to fill out a form with 465 fields and reproduce it in triplicate and file it to the right people and it's just a mess and super hard and nobody wants to do that.`}</p>
    <p>{`Okay and you can make it easier with software?`}</p>
    <p>{`Yes! I can build a beautiful flow and a wonderful UI that guides them through needing just 250 fields it's gonna be amazing!`}</p>
    <p>{`And you need an engineer for that?`}</p>
    <p>{`Well I can't build it myself.`}</p>
    <p>{`Mate, you need a google form and some customer research. Go sell a shitty version of your product. No code, no software, just your elbow grease and a smile.`}</p>
    <h2 {...{
      "id": "people-buy-your-solution-not-your-software"
    }}>{`People buy your solution, not your software`}</h2>
    <p>{`People don't really care `}<em parentName="p">{`how`}</em>{` you solve their problem. They just want somebody to do it for them.`}</p>
    <p>{`When I hire an accountant to do my taxes, I'm not buying the software they use, I'm buying peace of mind. The confidence that I won't overpay or underpay and that we'll find every legal loophole.`}</p>
    <p>{`When I pay for Spotify, I'm paying for ease of access to all the world's music. Their software, the player, just needs to be good enough.`}</p>
    <p>{`When I hire a lawyer to help me get a visa, I don't care what they use to make the process easier or their margins higher. I only care that I get my visa and Trump doesn't kick me out. That's it. That's what I'm paying for.`}</p>
    <p>{`And that was my advice to Matt.`}</p>
    <p><strong parentName="p">{`Build the service first`}</strong>{`. Solve somebody's problem. Make them a better human. `}<strong parentName="p">{`Get paid`}</strong>{`.`}</p>
    <p>{`Then, when you're ready, when you feel the inefficiency deep within your bones, `}<em parentName="p">{`then`}</em>{` build your software. You'll know what you need.`}</p>
    <p>{`Cheers,`}</p>
    <p>{`~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      